import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
} from "@angular/core";
import { Observable, ReplaySubject, Subject } from "rxjs";
import { ReminderFolder } from "../store";
import { ReminderPoolFacade } from "../store/facade";
import { ReminderFolderFlatNode, ReminderFolderNode } from "../../model";
import { FormControl, FormGroup } from "@angular/forms";
import { FlatTreeControl } from "@angular/cdk/tree";
import { MatTreeFlatDataSource, MatTreeFlattener } from "@angular/material/tree";
import { MatDialog } from "@angular/material/dialog";
import { TranslocoService } from "@jsverse/transloco";
import { FuseConfirmationService } from "@nx-workspace/fuse";
import { take, takeUntil } from "rxjs/operators";
import { CreateFolderComponent } from "../create-folder/create-folder.component";
import { EditFolderComponent } from "../edit-folder/edit-folder.component";

@Component({
    selector: "reminder-folder-picker",
    templateUrl: "./reminder-folder-picker.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FolderPickerComponent implements OnDestroy {
    private _unsubscribeAll = new Subject();

    isLoading$: Observable<boolean>;

    folders$: Observable<ReminderFolder[]>;

    isThereAnyData$: Observable<boolean>;

    filteredReminderFolders$: ReplaySubject<ReminderFolder[]> = new ReplaySubject<ReminderFolder[]>(1);

    ReminderFolders: ReminderFolderNode[] = [];

    flatNodeMap: Map<ReminderFolderFlatNode, ReminderFolderNode> = new Map();

    nestedNodeMap: Map<string, ReminderFolderFlatNode> = new Map();

    searchForm: FormGroup = new FormGroup({
        text: new FormControl(""),
    });

    private _transformer = (node: ReminderFolderNode, level) => {
        const flatNode = new ReminderFolderFlatNode();
        flatNode.level = level;
        flatNode.expandable = node.children && node.children?.length > 0;
        flatNode.title = node.title;
        flatNode.parentId = node.parentId;
        flatNode.data = node.data;
        this.flatNodeMap.set(flatNode, node);
        this.nestedNodeMap.set(node.title, flatNode);
        return flatNode;
    };

    treeControl = new FlatTreeControl<ReminderFolderFlatNode>(
        (node) => node.level,
        (node) => node.expandable
    );

    treeFlattener = new MatTreeFlattener(
        this._transformer,
        (node) => node.level,
        (node) => node.expandable,
        (node) => node.children
    );

    dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    hasChild = (_: number, node: ReminderFolderFlatNode) => node.expandable;

    @Output()
    onFolderSelected = new EventEmitter<ReminderFolder>();

    @Input()
    selected: ReminderFolder;

    constructor(
        private _changeDetector: ChangeDetectorRef,
        private _matDialog: MatDialog,
        private _translateService: TranslocoService,
        private _facade: ReminderPoolFacade,
        private _confirmationService: FuseConfirmationService
    ) {
        this.folders$ = this._facade.folders$;

        this.isThereAnyData$ = this._facade._isThereAnyData$;

        //this._facade.root();
    }

    ngOnInit(): void {
        this.filteredReminderFolders$.pipe(takeUntil(this._unsubscribeAll)).subscribe((folders) => {
            this.ReminderFolders = folders.map((folder) => {
                return new ReminderFolderNode(folder, folder.parentFolderId ?? 0);
            });
            this.dataSource.data = this.ReminderFolders;
            this.treeControl.expandAll();
            this._changeDetector.markForCheck();
        });

        this.folders$.pipe(takeUntil(this._unsubscribeAll)).subscribe((folders) => {
            this.filteredReminderFolders$.next(folders);
        });

        this.searchForm
            .get("text")
            .valueChanges.pipe(takeUntil(this._unsubscribeAll))
            .subscribe((text) => {
                this.applyFilter(text.toLowerCase());
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    reload(nodes: ReminderFolderNode[]) {
        nodes.forEach((node) => {
            if (node.children) {
                this.reload(node.children);
            }
        });
    }

    map(items: ReminderFolder[], parentId: number | null = null): ReminderFolderNode[] {
        return items.map((item) => {
            const node = new ReminderFolderNode(item, parentId);
            node.children = this.map(item.folders, item.parentFolderId);
            return node;
        });
    }

    createFolder(parentId: number = null) {
        //Without the timeout the text inside the Dialog will not gain the focus
        setTimeout(() => {
            this._matDialog.open(CreateFolderComponent, {
                panelClass: "dialog-v2",
                data: {
                    parentId,
                },
            });
        });
    }

    editFolder(event: any, folder: ReminderFolderFlatNode) {
        //Without the timeout the text inside the Dialog will not gain the focus
        setTimeout(() => {
            this._matDialog.open(EditFolderComponent, {
                panelClass: "dialog-v2",
                data: folder.data,
            });
        });
    }

    deleteFolder(event: any, folder: ReminderFolderFlatNode) {
        //Without the timeout the text inside the Dialog will not gain the focus
        setTimeout(() => {
            this._confirmationService
                .open({
                    title: this._translateService.translate("health.engage.protocol-pool.folders.delete.alert.title"),
                    message: this._translateService.translate(
                        "health.engage.protocol-pool.folders.delete.alert.message",
                        {
                            name: folder.data.name,
                        }
                    ),
                    actions: {
                        confirm: {
                            label: this._translateService.translate("ACTION_DELETE"),
                        },
                        cancel: {
                            label: this._translateService.translate("ACTION_CANCEL"),
                        },
                    },
                })
                .afterClosed()
                .subscribe((result) => {
                    if (result === "confirmed") {
                        this._facade.deleteFolder(folder.data.id);
                    }
                });
        });
    }

    applyFilter(text: string) {
        this.folders$.pipe(take(1)).subscribe((folders) => {
            let filteredFolders = this.filterFoldersByName(folders, text);
            this.filteredReminderFolders$.next(filteredFolders);
        });
    }

    filterFoldersByName(folders: ReminderFolder[], searchText: string) {
        let filteredFolders = [];
        folders.forEach((folder) => {
            if (folder.name.toLowerCase().includes(searchText)) {
                filteredFolders.push(folder);
            } else if (folder.folders) {
                let filteredSubFolders = this.filterFoldersByName(folder.folders, searchText);
                if (filteredSubFolders.length > 0) {
                    let filteredFolder = Object.assign({}, folder);
                    filteredFolder.folders = filteredSubFolders;
                    filteredFolders.push(filteredFolder);
                }
            }
        });
        return filteredFolders;
    }

    select(folder: ReminderFolder) {
        this.onFolderSelected.emit(folder);
    }
}
